import { reduce } from "lodash";
import type { IRulesGroup } from "app/pages/segments/redesigned-wizard/segment-wizard/types";
import { RuleTypes } from "app/pages/segments/redesigned-wizard/segment-wizard/types";
import type { FC } from "react";
import React, { useMemo } from "react";
import { RuleFilterRow } from "app/pages/segments/redesigned-wizard/segment-wizard/segment-rules-section/RuleFilterRow";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    OrSeparatorText,
    GroupTitle,
    SegmentRuleWrapper,
} from "app/pages/segments/redesigned-wizard/segment-wizard/segment-rules-section/segmentRulesStyles";
import { doAllRuleFiltersDefined } from "app/pages/segments/redesigned-wizard/segment-wizard/segmentWizardHelpers";
import { AllPagesChipPlaceholder } from "app/pages/segments/redesigned-wizard/segment-wizard/segment-rules-section/AllPagesChipPlaceholder";
import { FILTERS_OBJECT } from "app/pages/segments/redesigned-wizard/segment-wizard/constants";
import { sortBy } from "lodash";

export const RulesGroup: FC<IRulesGroup> = (props) => {
    const sortedFiltersArray = useMemo(() => {
        return sortBy(FILTERS_OBJECT, [(o) => o.order]);
    }, []);

    const {
        isMidTierUser,
        onCloseRule,
        domain,
        getUrlsPreview,
        hideUrlsPreview,
        hasExistingUrlsCompoundStringNoRobots,
        updateWordPredictionsIfNeeded,
        ruleType,
        rule,
        ruleIndex,
        isFinalGroup,
        displayForEmptyRule,
        nextRule,
        hasAtLeastOneRule,
        doExcludeRulesExist,
        isMidtierUserAndOneRuleCreated,
        resetActiveIndex,
    } = props;

    const shouldDisplayRuleTitle = useMemo(
        () => isFinalGroup || (ruleIndex > 0 && (displayForEmptyRule || hasAtLeastOneRule)),
        [isFinalGroup, ruleIndex, displayForEmptyRule, hasAtLeastOneRule],
    );

    const ruleFilterRowProps = {
        sortedFiltersArray,
        isMidTierUser,
        onCloseRule,
        domain,
        getUrlsPreview,
        hideUrlsPreview,
        hasExistingUrlsCompoundStringNoRobots,
        updateWordPredictionsIfNeeded,
        ruleType,
        rule,
        nextRule,
        ruleIndex,
        hasAtLeastOneRule,
        isMidtierUserAndOneRuleCreated,
        shouldDisplayRuleTitle,
        resetActiveIndex,
    };

    const shouldDisplayAllPagesPlaceholderChip = useMemo(
        () => doExcludeRulesExist && ruleType === RuleTypes.include && ruleIndex === 0 && !hasAtLeastOneRule,
        [doExcludeRulesExist, ruleType, ruleIndex, hasAtLeastOneRule],
    );

    let numOfDefinedFilters = reduce(
        rule,
        (sum, filterValue, filterType) => {
            if (filterType !== "type" && rule[filterType].length > 0) {
                sum += 1;
            }
            return sum;
        },
        0,
    );

    return (
        <SegmentRuleWrapper
            displayBorder={isFinalGroup}
            ruleIndex={ruleIndex}
            isEmptyRule={!hasAtLeastOneRule}
        >
            {shouldDisplayRuleTitle && (
                <GroupTitle>
                    {i18nFilter()("segmentsWizard.addFilters.groupName.label", {
                        groupNumber: ruleIndex + 1,
                    })}
                </GroupTitle>
            )}
            {hasAtLeastOneRule && (
                <>
                    {sortedFiltersArray.map(({ filterKey }) => {
                        if (rule[filterKey].length > 0) {
                            const displayOrLabel = numOfDefinedFilters > 1;
                            numOfDefinedFilters -= 1;
                            return (
                                <React.Fragment
                                    key={`ruleFilterRowWrapper_${ruleType}_${ruleIndex}_${filterKey}_${rule[filterKey].length}`}
                                >
                                    <RuleFilterRow
                                        filterType={filterKey}
                                        {...ruleFilterRowProps}
                                    />
                                    {displayOrLabel && (
                                        <OrSeparatorText>
                                            {i18nFilter()("segmentsWizard.addFilters.or.label")}
                                        </OrSeparatorText>
                                    )}
                                </React.Fragment>
                            );
                        }
                    })}
                    {!doAllRuleFiltersDefined(rule, isMidTierUser) && (
                        <RuleFilterRow
                            key={`ruleFilterRow_${ruleType}_${ruleIndex}_emptyRule`}
                            {...ruleFilterRowProps}
                        />
                    )}
                </>
            )}
            {!hasAtLeastOneRule && displayForEmptyRule && (
                <>
                    {shouldDisplayAllPagesPlaceholderChip && <AllPagesChipPlaceholder domain={domain} />}
                    <RuleFilterRow
                        key={`ruleFilterRow_${ruleType}_${ruleIndex}_emptyRule`}
                        {...ruleFilterRowProps}
                    />
                </>
            )}
        </SegmentRuleWrapper>
    );
};
